/**
 * Public payment-plan acceptance page.
 *
 * The link in the agreement email lands here with the opaque `token`.
 * No login required. Shows the plan terms + schedule + a single
 * "Accept this plan" button. Once accepted, the plan flips to `active`
 * and the backend scheduler takes over emailing instalment reminders.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Calendar, AlertCircle, X } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPaymentPlan() {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptedName, setAcceptedName] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/payment-plan/${token}`);
        setPlan(r.data);
        if (r.data.status === "active" || r.data.status === "completed") setAccepted(true);
      } catch (err) {
        setError(err?.response?.data?.detail || "This payment plan link is not valid or has expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/public/payment-plan/${token}/accept`, {
        accepted_name: acceptedName || null,
      });
      setAccepted(true);
      toast.success("Plan accepted! Your firm will be notified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Accept failed");
    } finally { setAccepting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ll-bg-page)] grid place-items-center">
        <Loader2 className="animate-spin text-[#0b5d9e]" size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ll-bg-page)] grid place-items-center p-6">
        <div className="bg-white border p-8 max-w-md text-center" style={{ borderColor: "var(--ll-border)" }} data-testid="plan-public-error">
          <X size={32} className="mx-auto text-red-500 mb-3" />
          <div className="font-display font-bold mb-1">Link no longer valid</div>
          <div className="text-sm text-[var(--ll-ink-soft)]">{error}</div>
        </div>
      </div>
    );
  }

  const inv = plan.invoice || {};
  const totalScheduled = (plan.schedule || []).reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="min-h-screen bg-[var(--ll-bg-page)] py-12 px-4" data-testid="public-payment-plan-page">
      <div className="mx-auto max-w-xl bg-white border shadow-lg" style={{ borderColor: "var(--ll-border)" }}>
        <div className="px-6 py-5 border-b bg-[#0b5d9e] text-white" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-75">Payment plan proposal</div>
          <div className="font-display font-bold text-xl">Invoice {inv.invoice_number}</div>
          <div className="text-sm opacity-90 mt-1">For {inv.bill_to_name}</div>
        </div>

        <div className="p-6 space-y-5 text-sm">
          {accepted ? (
            <div className="border-l-4 border-green-600 bg-green-50 text-green-800 px-4 py-3 flex items-start gap-2" data-testid="plan-accepted-banner">
              <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-bold">Plan accepted — thank you!</div>
                <div className="text-xs mt-0.5">
                  Reminders for each instalment will arrive by email as the due dates approach.
                  No further action is needed now.
                </div>
              </div>
            </div>
          ) : (
            <div className="border-l-4 border-[#0b5d9e] bg-[#EFF6FF] px-4 py-3 text-xs">
              Review the terms below and tap <strong>Accept this plan</strong> at the bottom to lock it in.
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Per instalment" value={`$${plan.amount_per_installment.toLocaleString()}`} />
            <Stat label="Every" value={`${plan.interval_days} days`} />
            <Stat label="Instalments" value={`${plan.installments_total}`} />
          </div>

          <div className="border" style={{ borderColor: "var(--ll-border)" }}>
            <div className="px-3 py-2 bg-[var(--ll-bg-card)] text-[10px] uppercase tracking-widest flex items-center gap-1">
              <Calendar size={11} /> Schedule
            </div>
            <ol className="divide-y" data-testid="plan-public-schedule">
              {(plan.schedule || []).map((s) => (
                <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm" style={{ borderColor: "var(--ll-border)" }}>
                  <span className="font-mono-fin">{s.due_date}</span>
                  <span className="font-mono-fin font-semibold">${s.amount.toLocaleString()}</span>
                  <StatusPill status={s.status} />
                </li>
              ))}
            </ol>
            <div className="px-3 py-2 border-t text-xs flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
              <span className="text-[var(--ll-ink-soft)]">Total scheduled</span>
              <span className="font-mono-fin font-bold">${totalScheduled.toLocaleString()}</span>
            </div>
          </div>

          <div className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">
            Invoice total <strong>${(inv.total || 0).toLocaleString()}</strong> (${((inv.total || 0) - (inv.paid_amount || 0)).toLocaleString()} outstanding).
            Instalments against the balance will be matched automatically as you pay.
          </div>

          {!accepted && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">
                  Your name (optional — for the record)
                </label>
                <input
                  type="text"
                  value={acceptedName}
                  onChange={(e) => setAcceptedName(e.target.value)}
                  placeholder="e.g. John Smith, Head of Finance"
                  maxLength={120}
                  className="w-full border px-3 py-2 text-sm"
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid="plan-public-name"
                />
              </div>
              <button
                type="button"
                onClick={accept}
                disabled={accepting}
                data-testid="plan-public-accept"
                className="w-full py-3 bg-[#0b5d9e] text-white font-display font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {accepting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Accept this plan
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div className="border p-2.5 bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
      <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">{label}</div>
      <div className="font-mono-fin font-semibold">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "#F3F4F6", fg: "#374151", label: "Pending" },
    paid:    { bg: "#DCFCE7", fg: "#166534", label: "Paid" },
    missed:  { bg: "#FEE2E2", fg: "#991B1B", label: "Missed" },
  };
  const m = map[status] || map.pending;
  return (
    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5" style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}
